import * as t from "io-ts";
import { BooleanFromString } from "io-ts-types/BooleanFromString";
import { UUID as uuidCodec } from "io-ts-types/UUID";
import { NumberFromString } from "io-ts-types/lib/NumberFromString";

import { currencyCodec } from "../codecs/currency";
import { dateFromStringCodec } from "../codecs/date";
import {
  feedItemResponseCodec,
  fundraiserCodec,
  fundraiserFeedCodec,
  nonprofitResponseCodec,
  nonprofitFeedResponseCodec,
  userResponseCodec,
  userFeedResponseCodec,
  fundraiserRaisedResponseCodec,
  tagResponseCodec,
  coverAssetCodec,
  nonEmptyMatchingCampaignBaseResponseCodec,
} from "../codecs/entities";
import { slugCodec } from "../codecs/nonprofit";
import { safeNonNegativeIntCodec } from "../codecs/number";
import { HttpMethod } from "../helpers/http";

import {
  listParamsCodec,
  listResponseCodec,
  makeCachedRouteSpec,
  makeRouteSpec,
} from ".";

export const fundraiserFullResponseCodec = t.type({
  fundraiser: fundraiserCodec,
  nonprofits: t.array(nonprofitResponseCodec),
  users: t.array(userResponseCodec),
  tags: t.array(tagResponseCodec),
  matchCampaigns: t.array(nonEmptyMatchingCampaignBaseResponseCodec),
});

export type FundraiserResponse = t.TypeOf<typeof fundraiserFullResponseCodec>;

const metadataPropsCodec = t.partial({
  brandColor: t.string,
  coverAssetOverride: t.union([coverAssetCodec, t.null]),
  coverYoutubeVideoUrl: t.union([t.string, t.null]),
  donationThankYouMessage: t.string,
  showInFeed: t.boolean,
  creatorApiKey: t.string,
  orgName: t.string,
  avatarCloudinaryId: t.string,
});
export const fundraisersFullResponseCodec = t.type({
  fundraisers: t.array(fundraiserCodec),
  nonprofits: t.array(nonprofitResponseCodec),
  users: t.array(userResponseCodec),
  tags: t.array(tagResponseCodec),
});

export type FundraisersFullResponse = t.TypeOf<
  typeof fundraisersFullResponseCodec
>;

// Light version of fundraisers response for feed endpoints
export const fundraisersFeedResponseCodec = t.type({
  fundraisers: t.array(fundraiserFeedCodec),
  nonprofits: t.array(nonprofitFeedResponseCodec),
  users: t.array(userFeedResponseCodec),
  tags: t.array(tagResponseCodec),
});

const identifiersTokenCodec = t.type({
  fundraiserIdentifier: t.union([uuidCodec, slugCodec]),
  nonprofitIdentifier: t.union([uuidCodec, slugCodec]),
});

export const deleteFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier",
  method: HttpMethod.DELETE,
  authenticated: true,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: t.type({}),
});

export const getFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.partial({ skipCache: BooleanFromString }),
  bodyCodec: t.type({}),
  responseBodyCodec: fundraiserFullResponseCodec,
  publicRoute: {
    publicCacheLengthMinutes: 0.1,
    alsoCacheIfAuthenticated: true,
  },
});

export const getFundraiserRaisedRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/raised",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.partial({ skipCache: BooleanFromString }),
  bodyCodec: t.type({}),
  responseBodyCodec: fundraiserRaisedResponseCodec,
  publicRoute: {
    publicCacheLengthMinutes: 0.1,
  },
});

export const postFundraiserBodyCodec = t.type({
  nonprofitId: uuidCodec,
  title: t.string,
  description: t.union([t.string, t.null]),
  startDate: t.union([dateFromStringCodec, t.null]),
  endDate: t.union([dateFromStringCodec, t.null]),
  goal: t.union([safeNonNegativeIntCodec, t.null]),
  raisedOffline: t.union([safeNonNegativeIntCodec, t.null]),
  currency: currencyCodec,
});
export const postFundraiserRouteSpecTemplate = {
  path: "/fundraiser",
  method: HttpMethod.POST,
  authenticated: true,
  tokensCodec: t.type({}),
  paramsCodec: t.type({}),
  responseBodyCodec: fundraiserFullResponseCodec,
};
export const postFundraiserRouteSpec = makeRouteSpec({
  ...postFundraiserRouteSpecTemplate,
  bodyCodec: t.intersection([
    postFundraiserBodyCodec,
    t.type({
      creatorNonprofitId: t.union([uuidCodec, t.null]),
      parentFundraiserId: t.union([uuidCodec, t.null]),
      coverImageCloudinaryId: t.union([t.string, t.null]),
      active: t.boolean,
      hidden: t.boolean,
      pinned: t.boolean,
      metadata: t.union([metadataPropsCodec, t.null]),
    }),
    t.partial({
      matchCampaign: t.type({
        maxTotalMatchAmount: safeNonNegativeIntCodec,
        maxMatchPerDonationOneTimeAmount: t.union([
          safeNonNegativeIntCodec,
          t.null,
        ]),
        startDate: t.union([dateFromStringCodec, t.null]),
        endDate: t.union([dateFromStringCodec, t.null]),
      }),
      forNonprofits: t.array(uuidCodec),
    }),
  ]),
});

export const updateFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier",
  method: HttpMethod.PATCH,
  authenticated: true,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.type({}),
  bodyCodec: t.partial({
    title: t.string,
    slug: t.string,
    description: t.union([t.string, t.null]),
    coverImageCloudinaryId: t.union([t.string, t.null]),
    active: t.boolean,
    hidden: t.boolean,
    startDate: t.union([dateFromStringCodec, t.null]),
    endDate: t.union([dateFromStringCodec, t.null]),
    goal: t.union([safeNonNegativeIntCodec, t.null]),
    raisedOffline: t.union([safeNonNegativeIntCodec, t.null]),
    currency: currencyCodec,
    pinned: t.boolean,
    metadata: t.union([metadataPropsCodec, t.null]),
    creatorNonprofitId: t.union([uuidCodec, t.null]),
  }),
  responseBodyCodec: fundraiserFullResponseCodec,
});

export const hideFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/hide",
  method: HttpMethod.PATCH,
  authenticated: true,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: fundraiserCodec,
});

export const unhideFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/unhide",
  method: HttpMethod.PATCH,
  authenticated: true,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: fundraiserCodec,
});

export const leaveFundraiserRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/leave",
  method: HttpMethod.PATCH,
  authenticated: true,
  tokensCodec: identifiersTokenCodec,
  paramsCodec: t.type({}),
  bodyCodec: t.type({}),
  responseBodyCodec: fundraiserCodec,
});

const entityFundraisersParamsCodec = t.intersection([
  listParamsCodec,
  t.partial({ publicFeed: BooleanFromString }),
]);

export const getAdminFundraisersRouteSpec = makeRouteSpec({
  path: "/nonprofit/:id/fundraisers/all",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: listParamsCodec,
  bodyCodec: t.type({}),
  responseBodyCodec: fundraisersFullResponseCodec,
});

export const getNonprofitFundraisersCachedRouteSpec = makeRouteSpec({
  path: "/nonprofit/:id/fundraisers",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: entityFundraisersParamsCodec, // TODO: replace with listParamsCodec after deploying #14941
  bodyCodec: t.type({}),
  responseBodyCodec: fundraisersFeedResponseCodec,
});

export const getUserFundraisersRouteSpec = makeRouteSpec({
  path: "/user/:id/fundraisers",
  method: HttpMethod.GET,
  authenticated: false,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: entityFundraisersParamsCodec,
  bodyCodec: t.type({}),
  responseBodyCodec: t.union([
    fundraisersFullResponseCodec,
    fundraisersFeedResponseCodec,
  ]),
});

const getFundraiserFeedParamsCodec = t.intersection([
  listParamsCodec,
  t.partial({ fundraiserId: uuidCodec }),
]);
const getFundraiserFeedBodyCodec = t.type({});
const getFundraiserFeedResponseBodyCodec = t.intersection([
  t.type({
    items: t.array(feedItemResponseCodec),
    users: t.array(userFeedResponseCodec),
  }),
  listResponseCodec,
]);
export const getFundraiserFeedRouteSpec = makeCachedRouteSpec({
  path: "/fundraiser/:id/feed",
  method: HttpMethod.GET,
  version: 0,
  tokensCodec: t.type({ id: uuidCodec }),
  paramsCodec: getFundraiserFeedParamsCodec,
  bodyCodec: getFundraiserFeedBodyCodec,
  responseBodyCodec: getFundraiserFeedResponseBodyCodec,
});

export const donationsBalanceResponseCodec = t.type({
  csvData: t.type({
    ok: t.array(t.array(t.string)),
    error: t.array(t.array(t.string)),
    hasMoreOk: t.boolean,
    hasMoreError: t.boolean,
  }),
});
export type DonationsBalanceResponse = t.TypeOf<
  typeof donationsBalanceResponseCodec
>;

export const getFundraiserCsvDataRouteSpec = makeRouteSpec({
  path: "/nonprofits/:nonprofitIdentifier/fundraiser/:fundraiserIdentifier/donationsBalance",
  tokensCodec: identifiersTokenCodec,
  method: HttpMethod.GET,
  authenticated: true,
  paramsCodec: t.partial({
    currency: currencyCodec,
    takeOk: NumberFromString,
    skipOk: NumberFromString,
    takeError: NumberFromString,
    skipError: NumberFromString,
  }),
  bodyCodec: t.type({}),
  responseBodyCodec: donationsBalanceResponseCodec,
});
